import { c as _c } from "react/compiler-runtime";
import { postHostedOnboardingPrefill } from '@onefootprint/fetch-hosted';
import { IcoChevronRight16, IcoFootprint24 } from '@onefootprint/icons';
import type { PostHostedOnboardingPrefillChallengeResponse, PrefillContext, PrefillPostRequest } from '@onefootprint/request-types';
import { BottomSheet } from '@onefootprint/ui';
import { useMutation } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { IDV_BODY_CONTENT_CONTAINER_ID, LEGACY_IDV_CONTAINER_ID } from '@/idv2/components/constants';
import { Challenge } from './challenge';
type PrefillOverlayProps = {
  authToken: string;
  onPrefill: () => Promise<void>;
  context: PrefillContext;
  challengeData?: PostHostedOnboardingPrefillChallengeResponse;
};
const PrefillOverlay = t0 => {
  const $ = _c(35);
  const {
    authToken,
    context,
    onPrefill,
    challengeData
  } = t0;
  const {
    t
  } = useTranslation("idv2");
  const [isExpanded, setIsExpanded] = useState(true);
  const scrollTo = _temp;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => {
      scrollTo("bottom");
      setIsExpanded(true);
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const handleExpand = t1;
  let t2;
  let t3;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => {
      scrollTo("bottom");
    };
    t3 = [];
    $[1] = t2;
    $[2] = t3;
  } else {
    t2 = $[1];
    t3 = $[2];
  }
  useEffect(t2, t3);
  let t4;
  if ($[3] !== authToken || $[4] !== onPrefill) {
    t4 = {
      mutationFn: async body => {
        await postHostedOnboardingPrefill({
          headers: {
            "X-Fp-Authorization": authToken
          },
          body
        });
        await onPrefill();
      }
    };
    $[3] = authToken;
    $[4] = onPrefill;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  const prefillMutation = useMutation(t4);
  let t5;
  if ($[6] !== prefillMutation.isPending) {
    t5 = () => {
      if (!prefillMutation.isPending) {
        scrollTo("top");
        setIsExpanded(false);
      }
    };
    $[6] = prefillMutation.isPending;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  const handleClose = t5;
  let t6;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <div className="h-[24px] w-[24px] bg-tertiary rounded-sm"><IcoFootprint24 color="septenary" /></div>;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  let t7;
  if ($[9] !== t) {
    t7 = t(_temp2);
    $[9] = t;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  let t8;
  if ($[11] !== t7) {
    t8 = <p className="text-label-2 text-secondary">{t7}</p>;
    $[11] = t7;
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  let t9;
  if ($[13] !== t) {
    t9 = t(_temp3);
    $[13] = t;
    $[14] = t9;
  } else {
    t9 = $[14];
  }
  let t10;
  if ($[15] !== t9) {
    t10 = <p className="text-body-2 text-tertiary">{t9}</p>;
    $[15] = t9;
    $[16] = t10;
  } else {
    t10 = $[16];
  }
  let t11;
  if ($[17] !== t10 || $[18] !== t8) {
    t11 = <div className="flex flex-row text-left gap-3">{t6}<div className="flex flex-col">{t8}{t10}</div></div>;
    $[17] = t10;
    $[18] = t8;
    $[19] = t11;
  } else {
    t11 = $[19];
  }
  let t12;
  if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <div className="flex items-center justify-center group-hover:translate-x-px transition-transform duration-100 max-w-[16px]"><IcoChevronRight16 color="primary" /></div>;
    $[20] = t12;
  } else {
    t12 = $[20];
  }
  let t13;
  if ($[21] !== t11) {
    t13 = <button className="flex flex-row justify-between bg-primary hover:bg-secondary transition-colors duration-100 cursor-pointer w-full group border border-primary rounded-sm p-3 shadow-sm mb-4" onClick={handleExpand} type="button">{t11}{t12}</button>;
    $[21] = t11;
    $[22] = t13;
  } else {
    t13 = $[22];
  }
  let t14;
  if ($[23] !== authToken || $[24] !== challengeData || $[25] !== context || $[26] !== prefillMutation) {
    t14 = <Challenge context={context} prefillMutation={prefillMutation} authToken={authToken} challengeData={challengeData} />;
    $[23] = authToken;
    $[24] = challengeData;
    $[25] = context;
    $[26] = prefillMutation;
    $[27] = t14;
  } else {
    t14 = $[27];
  }
  let t15;
  if ($[28] !== handleClose || $[29] !== isExpanded || $[30] !== t14) {
    t15 = <BottomSheet open={isExpanded} onClose={handleClose} portalId={IDV_BODY_CONTENT_CONTAINER_ID} overlayClassName="!absolute !h-full !w-full">{t14}</BottomSheet>;
    $[28] = handleClose;
    $[29] = isExpanded;
    $[30] = t14;
    $[31] = t15;
  } else {
    t15 = $[31];
  }
  let t16;
  if ($[32] !== t13 || $[33] !== t15) {
    t16 = <>{t13}{t15}</>;
    $[32] = t13;
    $[33] = t15;
    $[34] = t16;
  } else {
    t16 = $[34];
  }
  return t16;
};
export default PrefillOverlay;
function _temp(position) {
  const container = document.getElementById(LEGACY_IDV_CONTAINER_ID);
  if (container) {
    container.scrollTo({
      top: position === "bottom" ? container.scrollHeight : 0,
      behavior: "instant"
    });
  }
}
function _temp2($) {
  return $.collectData.prefill.registerFasterWithFootprint;
}
function _temp3($_0) {
  return $_0.collectData.prefill.preFillYourInfo;
}